import React from 'react';
import PropTypes from 'prop-types';
import {Backdrop, Modal, Button} from '@mui/material';
import {useIntl} from 'react-intl';
import logos from '../../../assets/images/company-logos';
import useStyles from './styles';

/**
 * ApprovalDisclaimerModal - A modal for displaying approval disclaimers
 * that require user confirmation before proceeding with offer selection.
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onConfirm - Called when user clicks the confirm button
 * @param {function} onDismiss - Called when user dismisses (backdrop click or X)
 * @param {object} disclaimerConfig - Config from approvalDisclaimers.js
 */
function ApprovalDisclaimerModal({open, onConfirm, onDismiss, disclaimerConfig}) {
  const {classes} = useStyles();
  const intl = useIntl();

  if (!disclaimerConfig) {
    return null;
  }

  const {titleKey, textKey, logo} = disclaimerConfig;
  const logoSrc = logos[logo];

  return (
    <Modal
      open={open}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{timeout: 500}}
      onClose={onDismiss}
    >
      <div className={classes.modalContent}>
        <div className={classes.closeButton} onClick={onDismiss}>
          <span className={classes.closeX}>×</span>
        </div>
        
        {logoSrc && (
          <div className={classes.logoWrapper}>
            <img src={logoSrc} alt='' className={classes.logo} />
          </div>
        )}
        
        <div className={classes.title}>
          {intl.formatMessage({id: titleKey})}
        </div>
        
        <div className={classes.text}>
          {intl.formatMessage({id: textKey})}
        </div>
        
        <div className={classes.buttonWrapper}>
          <Button
            variant='contained'
            color='primary'
            className={classes.confirmButton}
            onClick={onConfirm}
          >
            {intl.formatMessage({id: 'approvalDisclaimers.confirm'})}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

ApprovalDisclaimerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  disclaimerConfig: PropTypes.shape({
    provider: PropTypes.string.isRequired,
    countries: PropTypes.arrayOf(PropTypes.string).isRequired,
    titleKey: PropTypes.string.isRequired,
    textKey: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
  }),
};

ApprovalDisclaimerModal.defaultProps = {
  disclaimerConfig: null,
};

export default ApprovalDisclaimerModal;
